import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Copy,
  Download,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Clock,
  DollarSign,
  CreditCard,
  Receipt,
  Calendar,
  Tag,
  Activity,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  superAdminBillingService,
  type BillingOverview,
} from "@/services/superAdminBillingService";
import BillingTimeline from "@/components/super-admin/billing/BillingTimeline";
import InvoicesTable from "@/components/super-admin/billing/InvoicesTable";
import PaymentMethods from "@/components/super-admin/billing/PaymentMethods";
import PromotionsDisplay from "@/components/super-admin/billing/PromotionsDisplay";

type Props = {
  organizationId: string;
};

export default function OrganizationBillingTabEnhanced({
  organizationId,
}: Props) {
  const { toast } = useToast();
  const { organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [syncing, setSyncing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastInvoiceCursor, setLastInvoiceCursor] = useState<
    string | undefined
  >();

  useEffect(() => {
    fetchBillingOverview();
  }, [organizationId]);

  const fetchBillingOverview = async (cursor?: string) => {
    if (!organization?.id) return;

    try {
      const isLoadingMore = !!cursor;
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const { data, error } = await superAdminBillingService.getBillingOverview(
        organizationId,
        organization.id,
        { cursor },
      );

      if (error) throw error;
      if (!data) return;

      if (isLoadingMore && overview) {
        // Append new invoices to existing ones
        setOverview({
          ...overview,
          invoices: [...overview.invoices, ...data.invoices],
        });
      } else {
        setOverview(data);
      }

      // Set cursor for next page if we got full page of invoices
      if (data.invoices.length === 20) {
        setLastInvoiceCursor(data.invoices[data.invoices.length - 1]?.id);
      } else {
        setLastInvoiceCursor(undefined);
      }
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Failed to load billing overview",
        description: e?.message || "Could not fetch billing data",
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSync = async () => {
    if (!organization?.id) return;

    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "super-admin-stripe-reconcile",
        {
          body: {
            organizationId,
            apply: true,
            requestingOrgId: organization.id,
          },
        },
      );
      if (error) throw error;

      toast({
        title: "Synchronized",
        description: "Billing data has been reconciled with Stripe",
      });

      // Refresh the overview
      await fetchBillingOverview();
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Sync failed",
        description: e?.message || "Could not sync billing data",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = (format: "csv" | "json") => {
    if (!overview) return;

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `billing-${organizationId}-${timestamp}.${format}`;

    if (format === "csv") {
      const csv = superAdminBillingService.exportToCSV(overview);
      superAdminBillingService.downloadFile(csv, filename, "csv");
    } else {
      const json = superAdminBillingService.exportToJSON(overview);
      superAdminBillingService.downloadFile(json, filename, "json");
    }

    toast({
      title: "Exported",
      description: `Billing data exported as ${format.toUpperCase()}`,
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: `${label} copied to clipboard`,
    });
  };

  const getCurrentSubscription = () => {
    if (!overview?.subscriptions.length) return null;
    // Get the most relevant subscription
    const priority = ["active", "trialing", "past_due", "incomplete", "paused"];
    return overview.subscriptions.sort((a, b) => {
      const ai = priority.indexOf(a.status);
      const bi = priority.indexOf(b.status);
      return ai - bi;
    })[0];
  };

  const currentSub = getCurrentSubscription();

  if (loading && !overview) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                Billing Overview
                <Badge
                  variant={
                    currentSub?.status === "active"
                      ? "default"
                      : currentSub?.status === "trialing"
                        ? "secondary"
                        : "outline"
                  }
                >
                  {currentSub?.status || "No subscription"}
                </Badge>
              </CardTitle>
              <div className="text-sm text-gray-500">
                {overview?.customer?.email || "No billing email"}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport("csv")}
                disabled={!overview}
              >
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport("json")}
                disabled={!overview}
              >
                <Download className="h-4 w-4 mr-1" />
                JSON
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? (
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Sync
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-gray-500 mb-1">Customer ID</div>
              <div className="flex items-center gap-2">
                <code className="text-xs truncate">
                  {overview?.customer?.id || "-"}
                </code>
                {overview?.customer?.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      copyToClipboard(overview.customer!.id, "Customer ID")
                    }
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Current Period</div>
              <div className="text-sm">
                {currentSub
                  ? superAdminBillingService.formatDate(
                      currentSub.current_period_end,
                    )
                  : "-"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Trial End</div>
              <div className="text-sm">
                {currentSub?.trial_end
                  ? superAdminBillingService.formatDate(currentSub.trial_end)
                  : "-"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Last Webhook</div>
              <div className="text-sm">
                {overview?.diagnostics.last_webhook_timestamp
                  ? new Date(
                      overview.diagnostics.last_webhook_timestamp,
                    ).toLocaleString()
                  : "Never"}
              </div>
            </div>
          </div>

          {/* Parity Check Alert */}
          {overview?.diagnostics.parity.mismatches.length > 0 && (
            <Alert className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Billing data mismatch detected in:{" "}
                {overview.diagnostics.parity.mismatches.join(", ")}. Consider
                running sync.
              </AlertDescription>
            </Alert>
          )}

          {/* Upcoming Invoice Preview */}
          {overview?.upcoming_invoice && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    Upcoming Invoice
                  </span>
                </div>
                <span className="font-medium text-blue-900">
                  {superAdminBillingService.formatCurrency(
                    overview.upcoming_invoice.amount_due,
                    overview.upcoming_invoice.currency,
                  )}
                </span>
              </div>
              {overview.upcoming_invoice.lines.some((l) => l.proration) && (
                <div className="text-xs text-blue-700 mt-1">
                  Includes proration adjustments
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for different sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 md:grid-cols-6 w-full md:w-auto">
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <Activity className="h-3 w-3" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex items-center gap-1">
            <Receipt className="h-3 w-3" />
            Invoices
          </TabsTrigger>
          <TabsTrigger
            value="subscriptions"
            className="flex items-center gap-1"
          >
            <Calendar className="h-3 w-3" />
            Subscriptions
          </TabsTrigger>
          <TabsTrigger
            value="payment-methods"
            className="flex items-center gap-1"
          >
            <CreditCard className="h-3 w-3" />
            Payment
          </TabsTrigger>
          <TabsTrigger value="promotions" className="flex items-center gap-1">
            <Tag className="h-3 w-3" />
            Promotions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Diagnostics Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Diagnostics & Parity Check
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium mb-2">Stored Values</div>
                    <dl className="space-y-1">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Status:</dt>
                        <dd>
                          {overview?.diagnostics.parity.stored_status || "-"}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Plan ID:</dt>
                        <dd className="font-mono text-xs">
                          {overview?.diagnostics.parity.stored_plan_id || "-"}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Subscription:</dt>
                        <dd className="font-mono text-xs truncate max-w-[150px]">
                          {overview?.diagnostics.parity
                            .stored_subscription_id || "-"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                  <div>
                    <div className="font-medium mb-2">Effective Values</div>
                    <dl className="space-y-1">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Status:</dt>
                        <dd>
                          {overview?.diagnostics.parity.effective_status || "-"}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Plan ID:</dt>
                        <dd className="font-mono text-xs">
                          {overview?.diagnostics.parity.effective_plan_id ||
                            "-"}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Subscription:</dt>
                        <dd className="font-mono text-xs truncate max-w-[150px]">
                          {overview?.diagnostics.parity
                            .effective_subscription_id || "-"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {overview?.diagnostics.parity.mismatches.length > 0 ? (
                  <div className="flex items-center gap-2 text-amber-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">
                      Mismatches found in:{" "}
                      {overview.diagnostics.parity.mismatches.join(", ")}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">All values in sync</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Stripe Events */}
          {overview?.diagnostics.recent_stripe_events.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Recent Stripe Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overview.diagnostics.recent_stripe_events.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <div>
                        <Badge variant="outline" className="text-xs">
                          {event.type}
                        </Badge>
                      </div>
                      <div className="text-gray-500">
                        {superAdminBillingService.formatDateTime(event.created)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Billing Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <BillingTimeline
                events={overview?.timeline || []}
                loading={loading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <InvoicesTable
            invoices={overview?.invoices || []}
            loading={loading}
            onLoadMore={() => fetchBillingOverview(lastInvoiceCursor)}
            hasMore={!!lastInvoiceCursor}
          />
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-4">
          <div className="space-y-4">
            {overview?.subscriptions.map((sub) => (
              <Card key={sub.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Subscription {sub.id}
                    </CardTitle>
                    <Badge
                      variant={superAdminBillingService.getStatusBadgeVariant(
                        sub.status,
                      )}
                    >
                      {sub.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500 mb-1">Period</div>
                      <div>
                        {superAdminBillingService.formatDate(
                          sub.current_period_start,
                        )}{" "}
                        -{" "}
                        {superAdminBillingService.formatDate(
                          sub.current_period_end,
                        )}
                      </div>
                    </div>
                    {sub.trial_end && (
                      <div>
                        <div className="text-gray-500 mb-1">Trial End</div>
                        <div>
                          {superAdminBillingService.formatDate(sub.trial_end)}
                        </div>
                      </div>
                    )}
                    {sub.cancel_at_period_end && (
                      <div>
                        <div className="text-gray-500 mb-1">Cancels At</div>
                        <div>End of period</div>
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="text-sm font-medium mb-2">Items</div>
                    {sub.items.map((item) => (
                      <div key={item.id} className="text-sm text-gray-600">
                        {item.product_name} - {item.quantity}x{" "}
                        {item.price_amount &&
                          superAdminBillingService.formatCurrency(
                            item.price_amount,
                            item.price_currency || "usd",
                          )}
                        {item.price_interval && `/${item.price_interval}`}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            {(!overview?.subscriptions ||
              overview.subscriptions.length === 0) && (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No subscriptions found
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payment-methods" className="mt-4">
          <PaymentMethods
            paymentMethods={overview?.payment_methods || []}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="promotions" className="mt-4">
          <PromotionsDisplay
            subscriptions={overview?.subscriptions || []}
            loading={loading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
